export interface CoordinatePointerOverlayOptions {
  map: google.maps.Map;
  lat: number;
  lng: number;
  label: string;
  showInfoWindow?: boolean;
  onClose: () => void;
  iconUrl?: string;
}

export const COORDINATE_POINTER_ICON_URL =
  '/assets/images/map/marker/location-pin.svg';

const PIN_WIDTH = 40;
const PIN_HEIGHT = 40;
const TOOLTIP_HEIGHT = 44;
const PIN_TIP_OFFSET_X = PIN_WIDTH / 2;
const PIN_TIP_OFFSET_Y = TOOLTIP_HEIGHT;
const CLOSE_SELECTOR = '[data-coordinate-pointer-close]';

interface OverlayRenderState {
  label: string;
  iconUrl: string;
  showTooltip: boolean;
}

/**
 * Reusable Google Maps overlay: pin + optional coordinate tooltip on one row.
 */
export class CoordinatePointerOverlayHelper {
  private overlay: google.maps.OverlayView | null = null;
  private container: HTMLDivElement | null = null;
  private position: google.maps.LatLng | null = null;
  private onCloseHandler: (() => void) | null = null;
  private clickListener: ((event: Event) => void) | null = null;
  private renderState: OverlayRenderState | null = null;

  setPointer(options: CoordinatePointerOverlayOptions): void {
    if (typeof google === 'undefined') {
      return;
    }

    this.onCloseHandler = options.onClose;
    this.position = new google.maps.LatLng(options.lat, options.lng);
    this.renderState = {
      label: options.label,
      iconUrl: options.iconUrl ?? COORDINATE_POINTER_ICON_URL,
      showTooltip: options.showInfoWindow !== false,
    };

    if (!this.overlay) {
      this.createOverlay();
    }

    this.applyRenderState();
    this.overlay?.setMap(options.map);
    this.overlay?.draw();
  }

  clear(): void {
    this.overlay?.setMap(null);
    this.position = null;
    this.renderState = null;
    this.onCloseHandler = null;
  }

  private applyRenderState(): void {
    if (!this.container || !this.renderState) {
      return;
    }
    const { label, iconUrl, showTooltip } = this.renderState;
    this.container.innerHTML = buildOverlayHtml(label, iconUrl, showTooltip);
  }

  private createOverlay(): void {
    const helper = this;
    const containerRef = { current: null as HTMLDivElement | null };

    class PointerOverlay extends google.maps.OverlayView {
      onAdd(): void {
        const container = document.createElement('div');
        container.style.cssText =
          'position:absolute;pointer-events:none;width:max-content;max-width:none;';
        containerRef.current = container;
        helper.container = container;

        helper.clickListener = (event: Event) => {
          const target = (event.target as HTMLElement).closest(CLOSE_SELECTOR);
          if (target) {
            event.preventDefault();
            event.stopPropagation();
            helper.onCloseHandler?.();
          }
        };
        container.addEventListener('click', helper.clickListener);
        helper.applyRenderState();

        this.getPanes()?.floatPane.appendChild(container);
      }

      draw(): void {
        const container = containerRef.current;
        const projection = this.getProjection();
        if (!container || !projection || !helper.position) {
          return;
        }

        const point = projection.fromLatLngToDivPixel(helper.position);
        if (!point) {
          return;
        }

        container.style.left = `${point.x}px`;
        container.style.top = `${point.y}px`;
        container.style.transform = `translate(-${PIN_TIP_OFFSET_X}px, -${PIN_TIP_OFFSET_Y}px)`;
      }

      onRemove(): void {
        if (containerRef.current) {
          if (helper.clickListener) {
            containerRef.current.removeEventListener('click', helper.clickListener);
            helper.clickListener = null;
          }
          containerRef.current.remove();
        }
        containerRef.current = null;
        helper.container = null;
      }
    }

    this.overlay = new PointerOverlay();
  }
}

function buildOverlayHtml(
  label: string,
  iconUrl: string,
  showTooltip: boolean
): string {
  const safeLabel = escapeHtml(label);
  const safeIconUrl = escapeHtml(iconUrl);

  const tooltip = showTooltip
    ? `
      <div style="display:flex;flex-direction:row;align-items:center;flex-shrink:0;pointer-events:auto;">
        <div style="width:0;height:0;flex-shrink:0;border-top:8px solid transparent;border-bottom:8px solid transparent;border-right:9px solid #fff;filter:drop-shadow(-1px 0 1px rgba(13,28,75,0.08));"></div>
        <div style="height:44px;flex-grow:0;display:flex;flex-direction:row;justify-content:center;align-items:center;gap:10px;padding:12px 16px;border-radius:8px;background-color:#fff;box-shadow:0 2px 10px rgba(13,28,75,0.14);box-sizing:border-box;">
          <span style="height:20px;flex-grow:0;font-family:HelveticaNeue,'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:16px;font-weight:bold;line-height:1.25;letter-spacing:0.1px;color:#0d1c4b;white-space:nowrap;">${safeLabel}</span>
          <button type="button" data-coordinate-pointer-close aria-label="Close" style="display:flex;align-items:center;justify-content:center;border:none;background:transparent;cursor:pointer;padding:0;width:20px;height:20px;flex-shrink:0;color:#0d1c4b;">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="#0d1c4b" stroke-width="1.75" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>`
    : '';

  return `
    <div style="display:flex;flex-direction:row;flex-wrap:nowrap;align-items:center;width:max-content;box-sizing:border-box;">
      <div style="width:${PIN_WIDTH}px;height:${TOOLTIP_HEIGHT}px;flex-shrink:0;display:flex;align-items:flex-end;justify-content:center;pointer-events:none;">
        <img src="${safeIconUrl}" alt="" draggable="false" width="${PIN_WIDTH}" height="${PIN_HEIGHT}" style="width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;object-fit:contain;display:block;" />
      </div>
      ${tooltip}
    </div>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
